import { Shield, Lock, Clock, AlertCircle, CheckCircle, Sparkles } from 'lucide-react';
import { ServiceCategory } from './BookingFlow';
import { useEffect, useState } from 'react';
import { ZKPSetupFlow } from '../zkp/ZKPSetupFlow';
import { VerificationBadge } from '../zkp/VerificationBadge';

interface DataSharingConfirmationProps {
  category: ServiceCategory;
  professional: any;
  sharedData: string[];
  accessDuration: string;
  onDataChange: (data: string[]) => void;
  onDurationChange: (duration: string) => void;
}

const dataModules = {
  veterinary: [
    { id: 'medical', label: 'Full Medical History', description: 'Diagnoses, treatments, prescriptions', default: true },
    { id: 'vaccines', label: 'Vaccination Records', description: 'All vaccination history and certificates', default: true, zkpType: 'vaccination' as const },
    { id: 'medications', label: 'Current Medications', description: 'Active prescriptions and dosages', default: true, zkpType: 'medication-status' as const },
    { id: 'allergies', label: 'Allergies & Sensitivities', description: 'Known allergies and reactions', default: true, zkpType: 'allergy-presence' as const },
    { id: 'documents', label: 'Medical Documents', description: 'Test results, x-rays, reports', default: false }
  ],
  grooming: [
    { id: 'grooming', label: 'Grooming History', description: 'Past services and preferences', default: true },
    { id: 'temperament', label: 'Temperament Notes', description: 'Behavior during grooming', default: true },
    { id: 'allergies', label: 'Skin Allergies', description: 'Product sensitivities', default: true, zkpType: 'allergy-presence' as const },
    { id: 'identity', label: 'Basic Identity', description: 'Name, breed, age', default: true, zkpType: 'age-eligibility' as const }
  ],
  walking: [
    { id: 'behavior', label: 'Behavior Notes', description: 'Walking behavior and social skills', default: true },
    { id: 'emergency', label: 'Emergency Contact', description: 'Your contact information', default: true },
    { id: 'location', label: 'Location Access Notes', description: 'Gate codes, access instructions', default: true },
    { id: 'identity', label: 'Basic Identity', description: 'Name, breed, age', default: true, zkpType: 'age-eligibility' as const }
  ],
  boarding: [
    { id: 'vaccines', label: 'Vaccination Proof', description: 'Required for boarding', default: true, mandatory: true, zkpType: 'vaccination' as const },
    { id: 'medical', label: 'Medical Notes', description: 'Conditions and medications', default: true, zkpType: 'medication-status' as const },
    { id: 'feeding', label: 'Feeding Instructions', description: 'Diet and schedule', default: true },
    { id: 'behavior', label: 'Behavior Notes', description: 'Temperament and preferences', default: true },
    { id: 'emergency', label: 'Emergency Contact', description: 'Your contact information', default: true }
  ],
  training: [
    { id: 'behavior', label: 'Behavior History', description: 'Past behavior patterns', default: true },
    { id: 'vaccines', label: 'Vaccination Status', description: 'Required for training classes', default: true, zkpType: 'vaccination' as const },
    { id: 'identity', label: 'Basic Identity', description: 'Name, breed, age', default: true, zkpType: 'age-eligibility' as const }
  ],
  other: [
    { id: 'identity', label: 'Basic Identity', description: 'Name, breed, age', default: true }
  ]
};

export function DataSharingConfirmation({
  category,
  professional,
  sharedData,
  accessDuration,
  onDataChange,
  onDurationChange
}: DataSharingConfirmationProps) {
  const modules = dataModules[category] || [];
  const [sharingMode, setSharingMode] = useState<'privacy-protected' | 'full-share'>('privacy-protected');
  const [showZKPSetup, setShowZKPSetup] = useState(false);
  const [currentZKPModule, setCurrentZKPModule] = useState<any>(null);
  const [zkpVerifications, setZkpVerifications] = useState<Record<string, boolean>>({});

  // Initialize with defaults if empty - use useEffect to avoid setState during render
  useEffect(() => {
    if (sharedData.length === 0) {
      const defaults = modules.filter(m => m.default).map(m => m.id);
      onDataChange(defaults);
    }
  }, [category]); // Only run when category changes

  const toggleData = (moduleId: string) => {
    const module = modules.find(m => m.id === moduleId);
    if (module?.mandatory) return; // Can't toggle mandatory modules

    if (sharedData.includes(moduleId)) {
      onDataChange(sharedData.filter(id => id !== moduleId));
      // Also remove ZKP verification if exists
      if (zkpVerifications[moduleId]) {
        const newVerifications = { ...zkpVerifications };
        delete newVerifications[moduleId];
        setZkpVerifications(newVerifications);
      }
    } else {
      onDataChange([...sharedData, moduleId]);
    }
  };

  const handleUseZKP = (moduleId: string) => {
    const module = modules.find(m => m.id === moduleId);
    if (module?.zkpType) {
      setCurrentZKPModule(module);
      setShowZKPSetup(true);
    }
  };

  const handleZKPComplete = (proof: any) => {
    if (currentZKPModule) {
      setZkpVerifications({
        ...zkpVerifications,
        [currentZKPModule.id]: true
      });
      setShowZKPSetup(false);
      setCurrentZKPModule(null);
    }
  };

  if (showZKPSetup && currentZKPModule) {
    return (
      <ZKPSetupFlow
        verificationType={currentZKPModule.zkpType}
        context="booking"
        onComplete={handleZKPComplete}
        onCancel={() => {
          setShowZKPSetup(false);
          setCurrentZKPModule(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8" />
          <div>
            <h3 className="text-xl mb-1">Share Pet Information</h3>
            <p className="text-sm opacity-90">Control what data {professional.name} can access</p>
          </div>
        </div>
      </div>

      {/* Privacy Mode Selection - NEW */}
      <div>
        <h4 className="mb-3 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
          Choose Sharing Mode
        </h4>
        <div className="space-y-3">
          {/* Privacy-Protected Mode */}
          <label className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
            sharingMode === 'privacy-protected'
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500'
              : 'border-gray-200 dark:border-white/20 hover:border-gray-300 dark:hover:border-white/30'
          }`}>
            <div className="flex items-start gap-4">
              <input
                type="radio"
                name="sharing-mode"
                value="privacy-protected"
                checked={sharingMode === 'privacy-protected'}
                onChange={(e) => setSharingMode(e.target.value as any)}
                className="mt-1 w-5 h-5 text-purple-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <h5 className="font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Privacy-Protected Verification (Recommended)
                  </h5>
                </div>
                <p className="text-sm text-[#464452] dark:text-white/90 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Share verification badges only. Professional sees "✅ Verified" status without accessing your private records.
                </p>
                <div className="bg-white dark:bg-[#0F0E16] rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>Maximum privacy protection</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>Meets professional requirements</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>No sensitive data exposure</span>
                  </div>
                </div>
              </div>
            </div>
          </label>

          {/* Full Share Mode */}
          <label className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
            sharingMode === 'full-share'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
              : 'border-gray-200 dark:border-white/20 hover:border-gray-300 dark:hover:border-white/30'
          }`}>
            <div className="flex items-start gap-4">
              <input
                type="radio"
                name="sharing-mode"
                value="full-share"
                checked={sharingMode === 'full-share'}
                onChange={(e) => setSharingMode(e.target.value as any)}
                className="mt-1 w-5 h-5 text-blue-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <h5 className="font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Full Data Sharing
                  </h5>
                </div>
                <p className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Share complete records with explicit consent. Professional can view full details.
                </p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Privacy-Protected Verifications */}
      {sharingMode === 'privacy-protected' && (
        <div>
          <h4 className="mb-3 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Select Verifications to Create
          </h4>
          <p className="text-sm mb-4 text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
            Professional will see verification badges (✅) instead of your private data
          </p>
          <div className="space-y-3">
            {modules.filter(m => m.zkpType).map(module => {
              const isSelected = sharedData.includes(module.id);
              const isVerified = zkpVerifications[module.id];
              const isMandatory = module.mandatory;

              return (
                <div
                  key={module.id}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    isSelected && isVerified
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-500'
                      : isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                      : 'border-gray-200 dark:border-white/20'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <label className="flex items-start gap-3 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleData(module.id)}
                        disabled={isMandatory}
                        className="w-5 h-5 text-blue-600 rounded mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                            {module.label}
                          </span>
                          {isMandatory && (
                            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-semibold">
                              Required
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {module.description}
                        </div>
                      </div>
                    </label>
                  </div>

                  {isSelected && (
                    <div className="ml-8">
                      {isVerified ? (
                        <div className="flex items-center gap-3 p-3 bg-white dark:bg-[#0F0E16] rounded-lg">
                          <VerificationBadge type={module.zkpType!} verified={true} size="md" showTooltip={false} />
                          <span className="text-sm flex-1 text-[#059669] dark:text-green-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Privacy-protected verification created
                          </span>
                          <button
                            onClick={() => {
                              const newVerifications = { ...zkpVerifications };
                              delete newVerifications[module.id];
                              setZkpVerifications(newVerifications);
                            }}
                            className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleUseZKP(module.id)}
                          className="w-full px-4 py-3 rounded-lg text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                          style={{ background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                        >
                          <Shield className="w-4 h-4" />
                          Create Privacy-Protected Verification
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-xl p-5 mt-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                <strong className="text-[#2D2F7E] dark:text-white">What {professional.name} will see:</strong>
                <ul className="mt-2 space-y-1">
                  <li>• Verification badges (✅ Vaccinated, ✅ Age Eligible, etc.)</li>
                  <li>• "Verified without accessing private records" tooltip</li>
                  <li>• No medical documents, no personal details</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Data Modules (only if full-share mode) */}
      {sharingMode === 'full-share' && (
        <div>
          <h4 className="mb-4 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Select Information to Share
          </h4>
          <div className="space-y-3">
            {modules.map(module => {
              const isSelected = sharedData.includes(module.id);
              const isMandatory = module.mandatory;

              return (
                <label
                  key={module.id}
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                      : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
                  } ${isMandatory ? 'opacity-75' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleData(module.id)}
                    disabled={isMandatory}
                    className="w-5 h-5 text-blue-600 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {module.label}
                      </span>
                      {isMandatory && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-semibold">
                          Required
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {module.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Critical Trust Message */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6">
        <h4 className="mb-3 flex items-center gap-2 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
          <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span>Your Data, Your Control</span>
        </h4>
        <ul className="text-sm space-y-2 text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>✓ You decide exactly what to share</li>
          <li>✓ Access is time-limited and revocable</li>
          <li>✓ All access is logged on blockchain</li>
          <li>✓ Professional cannot edit identity fields</li>
          <li>✓ Automatic access expiry after appointment</li>
        </ul>
      </div>

      {/* Access Duration */}
      <div>
        <h4 className="mb-4 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
          Access Duration
        </h4>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500">
            <input
              type="radio"
              name="duration"
              value="appointment-only"
              checked={accessDuration === 'appointment-only'}
              onChange={(e) => onDurationChange(e.target.value)}
              className="w-5 h-5 text-blue-600 mt-0.5"
            />
            <div>
              <div className="mb-1 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                Appointment Only (Recommended)
              </div>
              <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                Access expires automatically 2 hours after appointment ends
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30">
            <input
              type="radio"
              name="duration"
              value="24-hours"
              checked={accessDuration === '24-hours'}
              onChange={(e) => onDurationChange(e.target.value)}
              className="w-5 h-5 text-blue-600 mt-0.5"
            />
            <div>
              <div className="mb-1 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                24 Hours
              </div>
              <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                Professional can access data for 24 hours from appointment time
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30">
            <input
              type="radio"
              name="duration"
              value="manual"
              checked={accessDuration === 'manual'}
              onChange={(e) => onDurationChange(e.target.value)}
              className="w-5 h-5 text-blue-600 mt-0.5"
            />
            <div>
              <div className="mb-1 font-semibold text-[#2D2F7E] dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                Until Manually Revoked
              </div>
              <div className="text-sm text-[#464452] dark:text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                Access remains until you manually revoke it
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
          <strong className="text-green-800 dark:text-green-300" style={{ fontFamily: 'Sora, sans-serif' }}>Data Sharing Summary</strong>
        </div>
        <div className="text-sm text-green-800 dark:text-green-300 space-y-1" style={{ fontFamily: 'Inter, sans-serif' }}>
          {sharingMode === 'privacy-protected' ? (
            <>
              <div>• Sharing {Object.keys(zkpVerifications).length} privacy-protected verifications</div>
              <div>• {professional.name} sees verification badges only (no private data)</div>
            </>
          ) : (
            <>
              <div>• Sharing {sharedData.length} data modules with {professional.name}</div>
              <div>• Full records will be accessible</div>
            </>
          )}
          <div>• Access duration: {accessDuration === 'appointment-only' ? 'Appointment only' : accessDuration === '24-hours' ? '24 hours' : 'Manual revocation'}</div>
          <div>• You can revoke access anytime from your dashboard</div>
          <div>• All access is logged and auditable</div>
        </div>
      </div>

      {/* Warning for sensitive categories */}
      {category === 'walker' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div className="text-sm text-yellow-800 dark:text-yellow-300" style={{ fontFamily: 'Inter, sans-serif' }}>
            <strong>Security Note:</strong> For pet walkers, we recommend only sharing minimal necessary information. 
            Location access notes should not include sensitive personal information.
          </div>
        </div>
      )}
    </div>
  );
}